// © Microsoft Corporation. All rights reserved.

import {CommentBoxStyles} from "./comment/comment-box/CommentBox.styles";
import { CommentItemStyles } from "./comment/comment-item/CommentItem.styles";
import {CommentListStyles} from "./comment/comment-list/CommentList.styles";
import {CommentStyles} from "./comment/Comment.styles";
import { CommentSuperAppStyles } from "./comment/themes/superapp/CommentSuperApp.styles";
import {CommonStyles} from "./utils/Common.styles";
import { CommonSuperAppStyles } from "./utils/themes/superapp/CommonSuperApp.styles";
import { CommunityGuidelinePageSuperAppStyles } from "./community-guideline/themes/superapp/CommunityGuidelinePageSuperApp.styles";
import { CommunityGuidelineStyles } from "./community-guideline/CommunityGuideline.styles";
import { ComponentStyles } from "@microsoft/fast-jss-manager";
import { ContentCardStyles } from "./common/content-card/ContentCard.styles";
import {ContentPanelStyles} from "./comment/content-panel/ContentPanel.styles";
import { ContentSocialBarStyles } from "./content-social-bar/ContentSocialBar.styles";
import { ContentSocialBarSuperAppStyles } from "./content-social-bar/themes/superapp/ContentSocialBarSuperApp.styles";
import { CopyrightFormStyles } from "./copyright-form/CopyrightForm.styles";
import { DeleteMenuMenuStyles } from "./common/delete-menu/DeleteMenu.styles";
import { DesignSystem } from "@microsoft/fast-components-styles-msft";
import {DisabledMessageStyles} from "./comment/disabled-message/DisabledMessage.styles";
import { FollowButtonStyles } from "./follow-button/FollowButton.styles";
import { FollowButtonSuperAppStyles } from "./follow-button/themes/superapp/FollowButtonSuperApp.styles";
import { GradientOverlayStyles } from "./common/gradient-overlay/GradientOverlay.styles";
import { MediaInputStyles } from "./ug-content/media-input/MediaInput.styles";
import { MiniReactionBreakdownStyles } from "./common/mini-reaction-breakdown/MiniReactionBreakdown.styles";
import { MostReactionsStyles } from "./common/most-reactions/MostReactions.styles";
import { MultiLoginPageStyles } from "./multi-login-page/MultiLoginPage.styles";
import { NavbarStyles } from "./common/navbar/Navbar.styles";
import { NotificationCardStyles } from "./common/notification/NotificationCard.styles";
import { NotificationTrayStyles } from "./common/notification/NotificationTray.styles";
import { OnboardingPageStyles } from "./onboarding-page/OnboardingPage.styles";
import { PopoverStyles } from "./common/popover/Popover.styles";
import { PopupStyles } from "./popup/Popup.styles";
import { ProfileActivityStyles } from "./profile/profile-activity/ProfileActivity.styles";
import { ProfileActivitySuperAppStyles } from "./profile/profile-activity/themes/superapp/ProfileActivitySuperApp.styles";
import { ProfileCardStyles } from "./profile/profile-card/ProfileCard.styles";
import { ProfileCardSuperAppStyles } from "./profile/profile-card/themes/superapp/ProfileCardSuperApp.styles";
import { ProfileCardUserStatsStyles } from "./profile/profile-card/profile-card-user-stats/ProfileCardUserStats.styles";
import { ProfileCardUserStatsSuperAppStyles } from "./profile/profile-card/profile-card-user-stats/themes/superapp/ProfileCardUserStatsSuperApp.styles";
import { ProfileCommentsStyles } from "./profile/profile-comments/ProfileComments.styles";
import { ProfileCommentsSuperAppStyles } from "./profile/profile-comments/themes/superapp/ProfileCommentsSuperApp.styles";
import { ProfileManageDataPageStyles } from "./profile/profile-manage-data-page/ProfileManageDataPage.styles";
import { ProfilePageStyles } from "./profile/profile-page/ProfilePage.styles";
import { ProfilePageSuperAppStyles } from "./profile/profile-page/themes/superapp/ProfilePageSuperApp.styles";
import { ProfilePostsStyles } from "./profile/profile-posts/ProfilePosts.styles";
import { ProfilePrivacyStyles } from "./profile/profile-privacy/ProfilePrivacy.styles";
import { ProfilePrivacySuperAppStyles } from "./profile/profile-privacy/themes/superapp/ProfilePrivacySuperApp.styles";
import { ProfileReportMenuStyles } from "./profile/profile-report-menu/ProfileReportMenu.styles";
import { ProfileReportMenuSuperAppStyles } from "./profile/profile-report-menu/themes/superapp/ProfileReportMenuSuperApp.styles";
import { ProfileSubscriptionStyles } from "./profile/profile-subscriptions/ProfileSubscriptions.styles";
import { ProfileTabsStyles } from "./profile/profile-tabs/ProfileTabs.styles";
import { ProfileTabsSuperAppStyles } from "./profile/profile-tabs/themes/superapp/ProfileTabsSuperApp.styles";
import { ProfileUsersStyles } from "./profile/profile-users/ProfileUsers.styles";
import { ProfileUsersSuperAppStyles } from "./profile/profile-users/themes/superapp/ProfileUsersSuperApp.styles";
import { PublisherProfileCardStatsStyles } from "./publisher-profile/publisher-profile-card/publisher-profile-card-stats/PublisherProfileCardStats.styles";
import { PublisherProfileCardStyles } from "./publisher-profile/publisher-profile-card/PublisherProfileCard.styles";
import { PublisherProfilePageStyles } from "./publisher-profile/publisher-profile-page/PublisherProfilePage.styles";
import { PublisherProfileTabsStyles } from "./publisher-profile/publisher-profile-tabs/PublisherProfileTabs.styles";
import { PublisherSubscribeFollowButtonStyles } from "./publisher-subscribe-follow-button/PublisherSubscribeFollowButton.styles";
import { ReactionBreakdownStyles } from "./reaction-breakdown/ReactionBreakdown.styles";
import { ReactionBreakdownSuperAppStyles } from "./reaction-breakdown/themes/superapp/ReactionBreakdownSuperApp.styles";
import { ReactionMenuStyles } from "./common/reaction-menu/ReactionMenu.styles";
import { ReactionPanelStyles } from "./common/reaction-panel/ReactionPanel.styles";
import { ReplyListStyles } from "./comment/reply-list/ReplyList.styles";
import { ReportMenuStyles } from "./common/report-menu/ReportMenu.styles";
import { ReportMenuSuperAppStyles } from "./common/report-menu/themes/superapp/ReportMenuSuperApp.styles";
import { SocialBarPanelStyles } from "./common/social-bar-panel/SocialBarPanel.styles";
import { SocialClassNameContract } from "./Social.classnames";
import { SortBoxStyles } from "./common/sort-box/SortBox.styles";
import { TagInputStyles } from "./ug-content/tag-input/TagInput.styles";
import { TypingUsersStyles } from "./comment/typing-users/TypingUsers.styles";
import { UGContentCreatePageStyles } from "./ug-content/ug-content-create-page/UGContentCreatePage.styles";
import { UGContentCreateView1Styles } from "./ug-content/ug-content-create-view1/UGContentCreateView1.styles";
import { UGContentCreateView2Styles } from "./ug-content/ug-content-create-view2/UGContentCreateView2.styles";
import { UGContentListStyles } from "./ug-content/ug-content-list/UGContentList.styles";
import { UserCardStyles } from "./comment/user-card/UserCard.styles";

/**
 * The Social styles
 * @type {ComponentStyles<SocialClassNameContract, DesignSystem>}
 */
export const SocialStyles: ComponentStyles < SocialClassNameContract, DesignSystem > = (config: DesignSystem) => ({
    ...CommonStyles(config),
    ...CommentStyles(config),
    ...ContentPanelStyles(config),
    ...CommentBoxStyles(config),
    ...CommentListStyles(config),
    ...CommentItemStyles(config),
    ...DisabledMessageStyles,
    ...ReplyListStyles(config),
    ...UserCardStyles(config),
    ...MiniReactionBreakdownStyles(config),
    ...PopoverStyles(config),
    ...ReactionMenuStyles(config),
    ...ReactionPanelStyles(config),
    ...ReportMenuStyles(config),
    ...SortBoxStyles(config),
    ...CommunityGuidelineStyles(config),
    ...ContentSocialBarStyles(config),
    ...ProfileUsersStyles(config),
    ...PopupStyles,
    ...NotificationCardStyles(config),
    ...NotificationTrayStyles(config),
    ...FollowButtonStyles,
    ...ReactionBreakdownStyles(config),
    ...ProfilePageStyles,
    ...ProfileActivityStyles(config),
    ...ProfileCardStyles(config),
    ...ProfileCommentsStyles(config),
    ...ProfilePrivacyStyles(config),
    ...ProfileTabsStyles(config),
    ...ProfilePostsStyles,
    ...MultiLoginPageStyles,
    ...ProfileReportMenuStyles,
    ...ProfileManageDataPageStyles(config),
    ...OnboardingPageStyles(config),
    ...DeleteMenuMenuStyles(config),
    ...UGContentCreatePageStyles(config),
    ...UGContentCreateView1Styles(config),
    ...UGContentCreateView2Styles(config),
    ...UGContentListStyles(config),
    ...TagInputStyles,
    ...MediaInputStyles,
    ...ContentCardStyles(config),
    ...TypingUsersStyles(config),
    ...GradientOverlayStyles(config),
    ...ProfileCardUserStatsStyles(config),
    ...CopyrightFormStyles(config),
    ...NavbarStyles(config),
    ...PublisherProfilePageStyles(config),
    ...PublisherProfileTabsStyles(config),
    ...PublisherProfileCardStyles(config),
    ...PublisherProfileCardStatsStyles(config),
    ...ProfileSubscriptionStyles(config),
    ...MostReactionsStyles(config),
    ...PublisherSubscribeFollowButtonStyles(config),

    /** SuperApp Styles */
    ...CommonSuperAppStyles(config),
    ...CommentSuperAppStyles(config),
    ...ProfilePageSuperAppStyles(config),
    ...ProfileCardUserStatsSuperAppStyles(config),
    ...ProfileTabsSuperAppStyles(config),
    ...ProfileCardSuperAppStyles(config),
    ...ProfileActivitySuperAppStyles(config),
    ...ProfileCommentsSuperAppStyles(config),
    ...FollowButtonSuperAppStyles(config),
    ...ProfileUsersSuperAppStyles(config),
    ...ProfilePrivacySuperAppStyles(config),
    ...ContentSocialBarSuperAppStyles(config),
    ...ReactionBreakdownSuperAppStyles(config),
    ...ProfileReportMenuSuperAppStyles(config),
    ...ReportMenuSuperAppStyles(config),
    ...CommunityGuidelinePageSuperAppStyles(config),
    ...SocialBarPanelStyles(config)
});